import { type ComponentProps, type ComponentType, type ReactNode, useId, useState } from "react";
import { EyeIcon, EyeOffIcon, InfoIcon, TriangleAlertIcon } from "lucide-react";

import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import { cn } from "@/lib/shadcn/utils";

type Icon = ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" }>;

export function CredentialSection({
    testId,
    title,
    description,
    action,
    children,
}: {
    testId: string;
    title: string;
    description: string;
    action?: ReactNode;
    children: ReactNode;
}) {
    const headingId = useId();
    return (
        <section data-testid={testId} aria-labelledby={headingId} className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="space-y-1">
                    <h2 id={headingId} className="text-lg font-semibold tracking-tight">
                        {title}
                    </h2>
                    <p className="text-sm text-muted-foreground">{description}</p>
                </div>
                {action}
            </div>
            <div className="overflow-hidden rounded-lg border bg-card text-card-foreground">{children}</div>
        </section>
    );
}

export function CredentialRow({
    icon: RowIcon,
    title,
    description,
    actions,
    children,
    ...rest
}: {
    icon: Icon;
    title: ReactNode;
    description: ReactNode;
    actions: ReactNode;
    children?: ReactNode;
} & ComponentProps<"div">) {
    return (
        <div {...rest}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/50 text-muted-foreground">
                    <RowIcon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1 basis-48">
                    <p data-testid="credential-title" className="truncate font-medium leading-snug">
                        {title}
                    </p>
                    <p data-testid="credential-description" className="text-sm text-muted-foreground">
                        {description}
                    </p>
                </div>
                <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">{actions}</div>
            </div>
            {children}
        </div>
    );
}

export function SectionMessage({ children, role }: { children: ReactNode; role?: "alert" }) {
    return (
        <p role={role} className={cn("px-4 py-8 text-center text-sm", role ? "text-destructive" : "text-muted-foreground")}>
            {children}
        </p>
    );
}

export function SecretInput({
    testId,
    secret,
    className,
    ...inputProps
}: { testId: string; secret: "password" | "API key" } & Omit<ComponentProps<"input">, "type">) {
    const [visible, setVisible] = useState(false);
    const VisibilityIcon = visible ? EyeOffIcon : EyeIcon;
    return (
        <div className="relative">
            <Input
                {...inputProps}
                data-testid={testId}
                type={visible ? "text" : "password"}
                spellCheck={false}
                className={cn("pr-10", className)}
            />
            <Button
                type="button"
                variant="ghost"
                size="icon"
                data-testid={`${testId}-visibility`}
                aria-label={`${visible ? "Hide" : "Show"} ${secret}`}
                aria-controls={inputProps.id}
                onClick={() => setVisible((shown) => !shown)}
                className="absolute inset-y-0 right-0 h-full w-10 rounded-l-none text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
                <VisibilityIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
        </div>
    );
}

export function Note({ tone = "info", children }: { tone?: "info" | "warning"; children: ReactNode }) {
    const NoteIcon = tone === "warning" ? TriangleAlertIcon : InfoIcon;
    return (
        <div
            className={cn(
                "flex gap-2.5 rounded-md border px-3 py-2.5 text-sm",
                tone === "warning" ? "border-amber-300 bg-amber-50 text-amber-800" : "bg-muted/50 text-muted-foreground",
            )}
        >
            <NoteIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {children}
        </div>
    );
}

export function FieldError({ id, testId, children }: { id: string; testId: string; children: ReactNode }) {
    return (
        <p id={id} data-testid={testId} role="alert" className="text-sm text-destructive first-letter:uppercase">
            {children}
        </p>
    );
}
